import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Extraction } from './extraction.interface';

@Injectable({
  providedIn: 'root',
})
export class ExtractionsService {
  constructor(private http: HttpClient) {}
  isEditEntities: boolean = false;

  /**
   * Download entities as a file.
   * CSV: field names as the header row, values as the data row(s). When every top-level value is
   * an object (e.g. one entry per invoice of a multi-invoice PDF) each becomes its own row, with a
   * leading "Record" column. Nested keys are joined with ' > ', arrays with '; '.
   */
  downloadEntities(entities: any, fileName: string | undefined, format: 'csv' | 'json'): void {
    const baseName = (fileName || 'extraction').replace(/\.[^.]+$/, '');
    let content: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(entities ?? {}, null, 2);
      mimeType = 'application/json';
    } else {
      const isRecord = (v: any) => v && typeof v === 'object' && !Array.isArray(v);
      const flatten = (obj: any, parentKey = '', out: Record<string, any> = {}) => {
        for (const key of Object.keys(obj || {})) {
          const value = obj[key];
          const fullKey = parentKey ? `${parentKey} > ${key}` : key;
          if (Array.isArray(value)) {
            out[fullKey] = value.map(v => (v && typeof v === 'object' ? JSON.stringify(v) : v ?? '')).join('; ');
          } else if (isRecord(value)) {
            flatten(value, fullKey, out);
          } else {
            out[fullKey] = value ?? '';
          }
        }
        return out;
      };
      const values = Object.values(entities || {});
      const rows: Record<string, any>[] = values.length && values.every(isRecord)
        ? Object.entries(entities).map(([record, value]) => ({ Record: record, ...flatten(value) }))
        : [flatten(entities)];
      const headers = [...new Set(rows.flatMap(row => Object.keys(row)))];
      const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      // BOM so Excel reads UTF-8 (non-English invoices)
      content = '﻿' + [headers, ...rows.map(row => headers.map(h => row[h]))]
        .map(r => r.map(escape).join(','))
        .join('\r\n');
      mimeType = 'text/csv;charset=utf-8';
    }

    const url = window.URL.createObjectURL(new Blob([content], { type: mimeType }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${baseName}_entities.${format}`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  getExtractions(params: any, body: any = {}): Observable<object> {
    return this.http.post('/api/extractions', body, { params });
  }

  getExtractionsTasks(params: any, body: any = {}): Observable<object> {
    return this.http.post('/api/extractions/tasks', body, { params });
  }

  updateExtractionsTasks(params: any, body: any = {}): Observable<object> {
    return this.http.put(`/api/extractions/tasks/${params.id}`, body, {
      params,
    });
  }

  updateExtractionStatus(params: any, body: any = {}): Observable<object> {
    return this.http.put(`/api/extractions/status/${params.id}`, body, {
      params,
    });
  }

  updateAllExtractionStatus(
    body: any = {},
    params: any = {}
  ): Observable<object> {
    return this.http.put(`/api/extractions/status`, body, {
      params,
    });
  }

  updateEntities = (body: any = {}, params: any = {}): Observable<object> => {
    return this.http.put(`/api/extractions/entities/${params.id}`, body, {});
  };

  uploadForExtraction(azureUploadResponse: any): Observable<any> {
    // Pass entire Azure upload response with fileId mapped from _id
    const body = {
      ...azureUploadResponse,
      fileId: azureUploadResponse._id || azureUploadResponse.fileId,
    };
    return this.http.post('/api/extractions/upload-for-extraction', body);
  }

  uploadForExtractionBatch(files: any[]): Observable<any> {
    const body = {
      files: files.map((f) => ({
        ...f,
        fileId: f._id || f.fileId,
      })),
    };
    return this.http.post('/api/extractions/upload-for-extraction-batch', body);
  }

  selectedExtractionForSearch: Extraction = {} as Extraction;
  getExtractionByMD5 = (md5: string) => {
    let body = {
      filters: {
        md5: md5,
      },
    };
    this.getExtractions({}, body).subscribe({
      next: (response: any) => {
        if (response && response.docs && response.docs.length > 0) {
          this.selectedExtractionForSearch = response.docs[0];
        } else {
          console.warn('No extraction found for the given MD5:', md5);
        }
      },
      error: (error: any) => {
        console.error('Error fetching extraction by MD5:', error);
      },
    });
  };

  /**
   * Get workflow instance by ID
   * @param workflowId - Workflow instance ID
   * @returns Observable of workflow data
   */
  getWorkflowById(workflowId: string): Observable<any> {
    return this.http.get(`/api/workflow/instances/${workflowId}`);
  }
}
